import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { productAdminService } from '../../services/api';
import { useToast } from '../../store/ToastContext';
import { formatNumber } from '../../utils/helpers';
import Button from '../../components/Button';
import LimitEditor from './LimitEditor';
import { SectionHeading, StatTile } from './AdminWidgets';
import OrganizationsTable from './OrganizationsTable';
import Pagination from './Pagination';
import ReportFilters from './ReportFilters';
import useProductAdminReports, { filterByName, sortRecentFirst } from './useProductAdminReports';
import styles from './ProductAdmin.module.css';
import t from './AdminTable.module.css';
import ui from './ProductAdminDashboard.module.css';
import {
  BuildingIcon, FileCheckIcon, FileTextIcon, PenIcon, ActivityIcon, SearchIcon, UsersIcon,
} from './ProductAdminIcons';

const PAGE_SIZE = 10;

export default function ProductAdminDashboard() {
  const navigate = useNavigate();
  const toast = useToast();
  const {
    filters, setFilters, companies, usage, loading, error, load, handleAuthError,
  } = useProductAdminReports({ period: 'month', withUsage: true });
  const [defaultLimit, setDefaultLimit] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  // Settings load on their own so the reports still show if this call fails
  useEffect(() => {
    productAdminService.settings()
      .then((response) => setDefaultLimit(response.data.data.documents_per_licence))
      .catch((err) => { if (!handleAuthError(err)) setDefaultLimit(null); });
  }, [handleAuthError]);

  const saveDefaultLimit = async (value) => {
    try {
      const response = await productAdminService.updateSettings({ documents_per_licence: value });
      setDefaultLimit(response.data.data.documents_per_licence);
      toast.success('Default documents per licence saved');
    } catch (err) {
      if (!handleAuthError(err)) toast.error(err.response?.data?.error || 'Could not save the default');
    }
  };

  // Most recently registered first, 10 per page
  const matching = filterByName(sortRecentFirst(companies), search);
  const lastPage = Math.max(1, Math.ceil(matching.length / PAGE_SIZE));
  const currentPage = Math.min(page, lastPage);
  const pageRows = matching.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div className={ui.container}>
      <header className={ui.pageHeader}>
        <h1 className={ui.title}>Product Usage</h1>
        <p className={ui.subtitle}>Organizations, document limits and signing activity.</p>
      </header>

      <ReportFilters filters={filters} setFilters={setFilters} onApply={load} loading={loading} />

      {error && <div className={styles.error}>{error}</div>}

      <section className={ui.statsGrid}>
        <StatTile tone="blue" title="Organizations" value={companies.length}
          hint="Registered companies" icon={<BuildingIcon />} />
        <StatTile tone="green" title="Documents signed" value={formatNumber(usage?.files)}
          hint="In the selected period" icon={<FileCheckIcon />} />
        <StatTile tone="amber" title="Signatures" value={formatNumber(usage?.signature_count)}
          hint="In the selected period" icon={<PenIcon />} />
        <StatTile tone="purple" title="Signing runs" value={formatNumber(usage?.events)}
          hint="In the selected period" icon={<ActivityIcon />} />
      </section>

      <section className={ui.panel}>
        <SectionHeading
          tone="teal"
          icon={<FileTextIcon />}
          title="Default Document Limit"
          text="Documents each licence can sign when an organization buys a plan. Existing organizations keep their current limits — change those on the organization's page."
        />
        {defaultLimit == null && !loading && (
          <p className={styles.hint}>The default limit could not be loaded. Check that the latest backend is deployed.</p>
        )}
        {defaultLimit != null && (
          <div className={styles.settingRow}>
            <LimitEditor value={defaultLimit} min={1} onSave={saveDefaultLimit} label="Documents per licence" />
            <span className={styles.hint}>
              documents per licence · e.g. 3 licences × {formatNumber(defaultLimit)} = {formatNumber(defaultLimit * 3)} documents
            </span>
          </div>
        )}
      </section>

      <section className={ui.panel}>
        <SectionHeading
          tone="blue"
          icon={<UsersIcon />}
          title="Recent Organizations"
          text="The latest organizations to register, with their plan and activity."
          action={(
            <Button variant="secondary" size="sm" className={t.softButton} onClick={() => navigate('/product-admin/organizations')}>
              View All
            </Button>
          )}
        />
        <div className={ui.searchBox}>
          <SearchIcon size={16} />
          <input
            className={ui.searchInput}
            placeholder="Search organization…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            aria-label="Search organization"
          />
        </div>
        {loading ? <p className={styles.loading}>Loading…</p> : (
          <>
            <OrganizationsTable companies={pageRows} searching={Boolean(search.trim())} />
            <Pagination page={currentPage} pageSize={PAGE_SIZE} total={matching.length} onChange={setPage} />
          </>
        )}
      </section>
    </div>
  );
}
